import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wrench,
  Cog,
  AlertTriangle,
  Cable,
  DoorOpen,
  Siren,
  ArrowUpRight,
  Clock4,
  ShieldCheck,
  Star,
  Phone,
  MapPin,
  Facebook,
  CalendarCheck,
} from "lucide-react";
import ServiceAreaMap from "@/components/ServiceAreaMap";
import HoursBadge from "@/components/HoursBadge";
import WorkGallery from "@/components/WorkGallery";
import { fetchServices } from "@/lib/api";
import { TOWNS } from "@/lib/towns";

const ICONS = {
  spring: Cog,
  opener: Wrench,
  panels: AlertTriangle,
  cables: Cable,
  new_door: DoorOpen,
  emergency: Siren,
};

const PHONE = "(361) 329-9550";
const PHONE_TEL = "+13613299550";
const BOOKING_URL =
  "https://app.squareup.com/appointments/book/l9pvy8kdwl7zdo/LNYM6MCNQE0AX/start";
const FACEBOOK_URL =
  "https://www.facebook.com/people/Coastal-Plains-Garage-Door-LLC/61594537055740/?sk=about";
const ASSET_BASE = "https://customer-assets-agu9un31.emergentagent.net/job_repair-assistant-19/artifacts";
const LOGO_URL = `${ASSET_BASE}/j7uenlxp_Coastal_Plains_Sticker-6.jpg`;
const LOGO_NEW_URL = `${process.env.PUBLIC_URL}/coastal-logo-new.png`;
const RWB_TEXAS_URL = `${ASSET_BASE}/8edf2d90029920b0_Coastal_Plains_RWB_Texas.jpg`;
const QR_URL = `${ASSET_BASE}/f85ce0ab77b43d40_Square_APT_QRL.png`;

const EXT = { target: "_blank", rel: "noopener noreferrer" };

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [selectedTown, setSelectedTown] = useState(null);

  const selectTown = (town) => {
    setSelectedTown((cur) => (cur?.name === town.name ? null : town));
    document
      .querySelector('[data-testid="service-area-map"]')
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  useEffect(() => {
    fetchServices()
      .then((d) => setServices(d.services || []))
      .catch(() => {});
  }, []);

  return (
    <div
      className="min-h-screen relative text-black flag-bg"
      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/tx-flag.svg)` }}
    >
      <div className="fixed inset-0 bg-white/90 pointer-events-none z-0" />

      <div className="relative z-10">
        <PhoneBar />
        <Header />

        {/* HERO */}
        <section className="relative border-b-2 border-black overflow-hidden">
          <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-16 flex flex-col items-center text-center">
            <div
              data-testid="hero-tagline"
              className="flex flex-col items-center gap-y-1 font-display font-bold text-xl sm:text-2xl md:text-3xl uppercase tracking-wide text-black mb-6"
            >
              <span className="flex items-center gap-3">
                <span className="inline-block w-3 h-3 bg-[#B22234] blink" />
                Serving the Coastal Bend
              </span>
              <span>Veteran Owned · <span className="text-[#B22234]">Texas Proud</span></span>
            </div>
            <h1 className="sr-only">Coastal Plains Garage Door Repair & Install</h1>
            <img
              src={LOGO_NEW_URL}
              alt="Coastal Plains Garage Door, LLC"
              data-testid="hero-logo"
              className="w-full max-w-2xl h-auto object-contain logo-fade-in"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-lg text-zinc-800 max-w-xl leading-relaxed">
                Broken springs, stuck openers, crashed doors — we fix all of it, fast. Local crew.
                Fair prices.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={`tel:${PHONE_TEL}`}
                  data-testid="hero-phone-cta"
                  className="bg-[#B22234] text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] hard-shadow-sm hard-shadow-hover inline-flex items-center gap-2"
                >
                  <Phone size={16} /> Call {PHONE}
                </a>
                <a
                  href={BOOKING_URL}
                  {...EXT}
                  data-testid="hero-online-booking-cta"
                  className="bg-black text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] hard-shadow-sm hard-shadow-hover inline-flex items-center gap-2"
                >
                  <CalendarCheck size={16} /> Online Booking <ArrowUpRight size={16} />
                </a>
              </div>
              <div className="mt-10 grid grid-cols-1 gap-4 max-w-md">
                <Stat n="Same-day" l="Most repairs" />
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <ServiceAreaMap selected={selectedTown} onReset={() => setSelectedTown(null)} />
            </div>
          </div>
          <Ticker />
        </section>

        {/* SERVICES */}
        <section id="services" className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600">/ What we do</div>
              <h2 className="font-display text-4xl md:text-5xl font-semibold uppercase tracking-tight mt-2">
                All work <span className="text-[#B22234]">guaranteed</span>.<br />
                Warranty on all parts.
              </h2>
            </div>
            <p className="max-w-md text-zinc-800">
              Residential and light commercial. Call or book online today.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = ICONS[s.slug] || Wrench;
              return (
                <div
                  key={s.slug}
                  data-testid={`service-card-${s.slug}`}
                  className="border border-black bg-white p-6 md:p-8 hard-shadow-hover fade-in-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="flex items-start justify-between">
                    <Icon size={32} strokeWidth={1.5} />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl font-semibold uppercase tracking-tight mt-6">
                    {s.title}
                  </h3>
                  <p className="text-zinc-700 text-sm mt-2">{s.desc}</p>
                  <a
                    href={BOOKING_URL}
                    {...EXT}
                    data-testid={`service-book-${s.slug}`}
                    className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] border-b border-black pb-1 hover:text-[#B22234] hover:border-[#B22234]"
                  >
                    Online Booking <ArrowUpRight size={12} />
                  </a>
                </div>
              );
            })}
          </div>
        </section>

        <WorkGallery />

        {/* PROCESS */}
        <section className="bg-black text-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400">/ How it works</div>
              <h2 className="font-display text-4xl md:text-5xl font-semibold uppercase tracking-tight mt-2">
                Fast. Fair. <span className="text-[#B22234]">Local</span>.
              </h2>
              <p className="text-zinc-300 mt-6 max-w-md">
                Call us or book online. A real Coastal Plains technician gives you an honest
                quote and shows up when we said we would.
              </p>
              <div className="mt-8 border border-white p-6 inline-block">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Dispatch hours</div>
                <div className="font-display text-2xl font-semibold mt-1">Mon — Sat · 07:00 – 21:00</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#B22234] mt-2">
                  24/7 emergency line: {PHONE}
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Step n="01" t="Call or book online" d="Pick a time that works for you." icon={Clock4} />
              <Step n="02" t="Get a flat quote" d="No surprise fees. Parts at cost." icon={ShieldCheck} />
              <Step n="03" t="Door fixed" d="Most repairs same-day. Warranty on every job." icon={Star} />
            </div>
          </div>
        </section>

        <ScanToBook />
        <ServiceTowns selected={selectedTown} onSelect={selectTown} />

        {/* FOOTER */}
        <footer className="border-t-2 border-black bg-white py-14">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
            <div className="md:col-span-1">
              <div className="border-2 border-black bg-white p-6 hard-shadow-sm inline-block">
                <img
                  src={LOGO_URL}
                  alt="Coastal Plains Garage Door, LLC"
                  className="h-40 w-auto object-contain"
                  data-testid="footer-logo"
                />
              </div>
            </div>

            <div className="font-mono text-sm text-zinc-800 space-y-2">
              <div className="uppercase tracking-widest text-zinc-500 text-[10px] mb-2">Contact</div>
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="footer-phone"
                className="flex items-center gap-2 font-display text-2xl font-semibold hover:text-[#B22234]"
              >
                <Phone size={20} /> {PHONE}
              </a>
              <div className="flex items-center gap-2 text-zinc-700">
                <MapPin size={14} /> Coastal Bend, Texas
              </div>
              <div className="text-zinc-700">Mon — Sat · 07:00 – 21:00</div>
              <div className="text-zinc-700">24/7 emergency line</div>
            </div>

            <div className="font-mono text-sm text-zinc-800 space-y-2">
              <div className="uppercase tracking-widest text-zinc-500 text-[10px] mb-2">Site</div>
              <a href="#services" className="block hover:text-[#B22234]" data-testid="footer-services-link">Services</a>
              <a href={BOOKING_URL} {...EXT} className="block hover:text-[#B22234]" data-testid="footer-online-booking-link">
                Online Booking
              </a>
              <a
                href={FACEBOOK_URL}
                {...EXT}
                data-testid="footer-facebook"
                className="flex items-center gap-2 hover:text-[#B22234]"
              >
                <Facebook size={14} /> Visit us on Facebook
              </a>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-6 md:px-12 mt-10 pt-6 border-t border-black flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <a
              href={FACEBOOK_URL}
              {...EXT}
              data-testid="footer-facebook-cta"
              className="inline-flex items-center gap-2 border-2 border-black bg-white px-4 py-3 font-mono text-xs uppercase tracking-[0.25em] hard-shadow-sm hard-shadow-hover hover:bg-[#B22234] hover:text-white transition-colors"
            >
              <Facebook size={16} /> Visit us on Facebook
            </a>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex flex-col md:flex-row md:items-center gap-2 md:gap-6">
              <span>© {new Date().getFullYear()} Coastal Plains Garage Door, LLC · All rights reserved</span>
              <span>Proudly serving the Texas Coastal Bend ⭐</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

function ScanToBook() {
  return (
    <section id="book" data-testid="scan-to-book" className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
      <div className="border-2 border-black bg-white hard-shadow-lg grid grid-cols-1 md:grid-cols-12 gap-10 items-center p-8 md:p-12">
        <div className="md:col-span-4 flex justify-center">
          <img
            src={QR_URL}
            alt="Scan to book an appointment"
            data-testid="booking-qr-code"
            className="w-56 h-56 md:w-64 md:h-64 object-contain border border-black p-2 bg-white"
          />
        </div>
        <div className="md:col-span-8">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600">/ Scan to book</div>
          <h2 className="font-display text-4xl md:text-5xl font-semibold uppercase tracking-tight mt-2">
            Book your <span className="text-[#B22234]">service</span> online.
          </h2>
          <p className="text-zinc-800 mt-4 max-w-lg">
            Scan the QR code with your phone camera, or tap the button below to pick a time that
            works for you.
          </p>
          <a
            href={BOOKING_URL}
            {...EXT}
            data-testid="scan-online-booking-btn"
            className="mt-8 bg-[#B22234] text-white px-8 py-5 font-mono text-sm uppercase tracking-[0.25em] hard-shadow-sm hard-shadow-hover inline-flex items-center gap-2"
          >
            <CalendarCheck size={18} /> Online Booking <ArrowUpRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}

function ServiceTowns({ selected, onSelect }) {
  return (
    <section
      id="service-towns"
      data-testid="service-towns"
      className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-20 text-center"
    >
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600">/ Towns we serve</div>
      <h2 className="font-display text-2xl md:text-3xl font-semibold uppercase tracking-tight mt-2">
        Garage door repair across the <span className="text-[#B22234]">Coastal Bend</span>
      </h2>
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-2">
        Tap a town to see it on the map
      </p>
      <ul className="mt-6 flex flex-wrap justify-center items-center gap-x-3 gap-y-2 font-display text-base md:text-lg text-zinc-900 max-w-4xl mx-auto">
        {TOWNS.map((t, i) => (
          <li key={t.name} className="flex items-center gap-3">
            {i > 0 && <span className="text-[#B22234]" aria-hidden="true">●</span>}
            <button
              type="button"
              onClick={() => onSelect(t)}
              data-testid={`service-town-${i}`}
              aria-pressed={selected?.name === t.name}
              className={`border-b-2 transition-colors hover:text-[#B22234] ${
                selected?.name === t.name ? "text-[#B22234] border-[#B22234]" : "border-transparent"
              }`}
            >
              {t.name}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PhoneBar() {
  return (
    <div
      data-testid="phone-bar"
      className="bg-[#B22234] text-white border-b-2 border-black relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-2.5 flex flex-col sm:flex-row items-center justify-between gap-1 font-mono text-[11px] uppercase tracking-[0.25em]">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 bg-white blink" />
          <span>24/7 emergency garage door service</span>
        </div>
        <HoursBadge testId="mobile-hours-badge" className="lg:hidden my-1 !py-1" />
        <a
          href={`tel:${PHONE_TEL}`}
          data-testid="top-phone-link"
          className="flex items-center gap-2 hover:text-black transition-colors font-semibold"
        >
          <Phone size={14} /> Call now · {PHONE}
        </a>
      </div>
    </div>
  );
}

function Header() {
  return (
    <header className="border-b border-black bg-white sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-3 flex items-center justify-between gap-2 sm:gap-4">
        <Link
          to="/"
          data-testid="logo"
          className="font-display text-base sm:text-xl md:text-2xl font-bold uppercase tracking-tight leading-none shrink-0"
        >
          <span className="block">Coastal Plains</span>
          <span className="block text-[#B22234] text-sm md:text-base">Garage Door, LLC</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 font-mono text-xs uppercase tracking-[0.2em]">
          <a href="#services" className="hover:text-[#B22234]" data-testid="header-services-link">Services</a>
          <a href="#book" className="hover:text-[#B22234]" data-testid="header-scan-link">Scan to book</a>
        </nav>
        <div className="flex items-center gap-3">
          <HoursBadge testId="header-hours-badge" className="hidden lg:inline-flex" />
          <a
            href={BOOKING_URL}
            {...EXT}
            data-testid="header-online-booking-btn"
            className="bg-black text-white px-3 sm:px-4 py-2.5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.15em] sm:tracking-[0.2em] whitespace-nowrap hover:bg-[#B22234] transition-colors inline-flex items-center gap-2"
          >
            <CalendarCheck size={14} /> Online Booking
          </a>
          <img
            src={RWB_TEXAS_URL}
            alt="Texas"
            data-testid="header-rwb-texas"
            className="h-9 sm:h-12 md:h-14 w-auto object-contain shrink-0"
          />
        </div>
      </div>
    </header>
  );
}

function Stat({ n, l }) {
  return (
    <div className="border-l-2 border-black pl-3">
      <div className="font-display text-2xl font-semibold">{n}</div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">{l}</div>
    </div>
  );
}

function Step({ n, t, d, icon: Icon }) {
  return (
    <div className="border border-white p-6 bg-black hover:bg-[#B22234] transition-colors">
      <div className="flex items-start justify-between">
        <Icon size={24} strokeWidth={1.5} />
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{n}</span>
      </div>
      <div className="font-display text-xl font-semibold uppercase tracking-tight mt-6">{t}</div>
      <p className="text-zinc-300 text-sm mt-2">{d}</p>
    </div>
  );
}

function Ticker() {
  const items = [
    "Broken spring? Call us",
    "Same-day service",
    "Veteran owned",
    "Coastal Bend, Texas",
    "Book online 24/7",
    "24/7 emergency line",
  ];
  const row = [...items, ...items];
  return (
    <div className="bg-black text-white border-t-2 border-black overflow-hidden">
      <div className="ticker py-3 flex gap-12 font-mono text-[11px] uppercase tracking-[0.3em]">
        {row.map((s, i) => (
          <span key={i} className="flex items-center gap-12">
            {s} <span className="text-[#B22234]">●</span>
          </span>
        ))}
      </div>
    </div>
  );
}
